import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CheckCheck, Clock, CalendarDays, AlertTriangle, UserX, Laptop, UserPlus, LogOut, Ban } from 'lucide-react';
import { notificationService } from '../services/dataService';
import io from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const typeIcons = {
  new_punch: Clock,
  leave_request: CalendarDays,
  leave_approved: Check,
  leave_rejected: AlertTriangle,
  late_arrival: Clock,
  absent: UserX,
  ot_warning: AlertTriangle,
  kiosk_offline: Laptop,
  leave_update: CalendarDays,
  visitor_checkin: UserPlus,
  visitor_checkout: LogOut,
  visitor_blacklist: Ban,
  visitor_approved: Check,
  visitor_rejected: AlertTriangle,
};

const typeColors = {
  new_punch: 'bg-blue-100 text-blue-600',
  leave_request: 'bg-amber-100 text-amber-600',
  leave_approved: 'bg-emerald-100 text-emerald-600',
  leave_rejected: 'bg-red-100 text-red-600',
  late_arrival: 'bg-orange-100 text-orange-600',
  absent: 'bg-red-100 text-red-600',
  ot_warning: 'bg-purple-100 text-purple-600',
  kiosk_offline: 'bg-gray-100 text-gray-600',
  leave_update: 'bg-blue-100 text-blue-600',
  visitor_checkin: 'bg-teal-100 text-teal-600',
  visitor_checkout: 'bg-cyan-100 text-cyan-600',
  visitor_blacklist: 'bg-red-100 text-red-600',
  visitor_approved: 'bg-emerald-100 text-emerald-600',
  visitor_rejected: 'bg-orange-100 text-orange-600',
};

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const socketRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationService.getAll();
      if (res?.success) {
        setNotifications(res.data?.notifications || []);
        setUnreadCount(res.data?.unreadCount || 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Get company context for company-scoped WebSocket rooms
    let companyId = localStorage.getItem('selectedCompanyId');
    let isSuperAdmin = false;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!companyId && user.companyId) companyId = String(user.companyId);
      isSuperAdmin = user.role === 'super_admin' || user.role === 'superadmin';
    } catch {}

    // Connect to WebSocket
    try {
      socketRef.current = io(BACKEND_URL.replace(/\/api\/?$/, ''), {
        path: '/socket.io',
        transports: ['websocket', 'polling']
      });

      socketRef.current.on('connect', () => {
        if (isSuperAdmin) {
          // SuperAdmin joins the superadmin room (sees all notifications)
          socketRef.current.emit('join-superadmin');
        } else {
          // Company admin joins company-scoped room
          socketRef.current.emit('join-admin', { companyId });
        }
      });

      socketRef.current.on('notification', (notif) => {
        setNotifications(prev => [notif, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
      });

      socketRef.current.on('new-punch', (punch) => {
        const notif = {
          id: Date.now(),
          type: 'new_punch',
          title: 'New Punch',
          message: `${punch.employee?.firstName} ${punch.employee?.lastName} punched ${punch.punchType}`,
          timestamp: punch.punchTime,
          read: false
        };
        setNotifications(prev => [notif, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
      });

      // VMS real-time events
      socketRef.current.on('visitor-checkin', (data) => {
        const names = data.visitors?.map(v => v.name).join(', ') || 'Visitor';
        const notif = {
          id: Date.now(),
          type: 'visitor_checkin',
          title: 'Visitor Check-In',
          message: data.count > 1 ? `${data.count} visitors checked in: ${names}` : `${names} checked in${data.purpose ? ` for ${data.purpose}` : ''}`,
          timestamp: new Date().toISOString(),
          read: false
        };
        setNotifications(prev => [notif, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
      });

      socketRef.current.on('visitor-checkout', (data) => {
        const notif = {
          id: Date.now() + 1,
          type: 'visitor_checkout',
          title: 'Visitor Check-Out',
          message: `${data.count || 1} visitor(s) checked out`,
          timestamp: new Date().toISOString(),
          read: false
        };
        setNotifications(prev => [notif, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
      });

      socketRef.current.on('visitor-approved', (data) => {
        const notif = {
          id: Date.now() + 2,
          type: 'visitor_approved',
          title: 'Visitor Approved',
          message: `Visitor log #${data.logId} approved`,
          timestamp: new Date().toISOString(),
          read: false
        };
        setNotifications(prev => [notif, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
      });

      socketRef.current.on('visitor-rejected', (data) => {
        const notif = {
          id: Date.now() + 3,
          type: 'visitor_rejected',
          title: 'Visitor Rejected',
          message: `Visitor log #${data.logId} rejected`,
          timestamp: new Date().toISOString(),
          read: false
        };
        setNotifications(prev => [notif, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
      });
    } catch {}

    return () => { socketRef.current?.disconnect(); };
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setShowDropdown(!showDropdown)} data-testid="notification-bell"
        className="relative p-2 rounded-xl hover:bg-accent transition-colors">
        <Bell className="w-5 h-5 text-foreground-muted" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div data-testid="notification-dropdown" className="absolute right-0 top-full mt-2 w-96 bg-card rounded-2xl shadow-2xl border border-border z-50 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-bold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-accent-foreground hover:opacity-80 flex items-center gap-1">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-border/50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-foreground-muted text-sm">No notifications yet</div>
            ) : notifications.slice(0, 15).map(notif => {
              const Icon = typeIcons[notif.type] || Bell;
              return (
                <div key={notif.id} className={`px-4 py-3 flex gap-3 hover:bg-accent/50 transition-colors ${!notif.read ? 'bg-accent/30' : ''}`}>
                  <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${typeColors[notif.type] || 'bg-muted text-foreground-muted'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{notif.title}</p>
                    <p className="text-xs text-foreground-muted truncate">{notif.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{timeAgo(notif.timestamp)}</p>
                  </div>
                  {!notif.read && <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
